import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Calendar, Footprints, Ruler, Shirt } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import useAuthStore from "@/store/use-auth-store";
import { isPersonaFitComplete } from "../lib/completeness";
import { usePersonaSetup } from "../hooks/use-persona-setup";
import { QuestCard } from "../components/QuestCard";

const SIZE_OPTIONS = [
  "XXS",
  "XS",
  "S",
  "M",
  "L",
  "XL",
  "XXL",
  "3XL",
  "4XL",
  "5XL",
] as const;

const fieldClass =
  "w-full h-11 px-3 rounded-lg border border-border-primary dark:border-gray-600 bg-white dark:bg-gray-900 text-text-primary dark:text-gray-100 text-base placeholder:text-text-placeholder dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-text-brand dark:focus:ring-indigo-400";

export const FitQuest: React.FC = () => {
  const { t } = useTranslation();
  const { saveFields, completeQuest, isSaving } = usePersonaSetup();
  const user = useAuthStore((state) => state.user);

  const [age, setAge] = useState(user?.age?.toString() || "");
  const [height, setHeight] = useState(user?.height?.toString() || "");
  const [size, setSize] = useState(user?.size || "");
  const [shoeSize, setShoeSize] = useState(user?.shoeSize?.toString() || "");

  const ready = isPersonaFitComplete({ age, height, size, shoeSize });

  return (
    <QuestCard>
      <div className="mb-5 flex flex-col gap-1">
        <h2 className="text-xl font-bold text-text-primary dark:text-gray-100 sm:text-2xl">
          {t("personaSetup.quests.fit.title")}
        </h2>
        <p className="text-sm text-text-tertiary dark:text-gray-400">
          {t("personaSetup.quests.fit.description")}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="flex min-w-0 flex-col gap-1.5">
          <span className="flex items-center gap-2 text-sm font-medium text-text-primary dark:text-gray-200">
            <Calendar className="h-4 w-4 text-text-brand dark:text-indigo-400" />
            {t("settings.profile.age")}
          </span>
          <input
            type="number"
            inputMode="numeric"
            min={13}
            max={120}
            value={age}
            onChange={(event) => setAge(event.target.value)}
            className={fieldClass}
            placeholder={t("settings.profile.agePlaceholder")}
          />
        </label>
        <label className="flex min-w-0 flex-col gap-1.5">
          <span className="flex items-center gap-2 text-sm font-medium text-text-primary dark:text-gray-200">
            <Ruler className="h-4 w-4 text-text-brand dark:text-indigo-400" />
            {t("settings.profile.height")}
          </span>
          <input
            type="number"
            inputMode="numeric"
            min={50}
            max={300}
            value={height}
            onChange={(event) => setHeight(event.target.value)}
            className={fieldClass}
            placeholder={t("settings.profile.heightPlaceholder")}
          />
        </label>
        <label className="flex min-w-0 flex-col gap-1.5">
          <span className="flex items-center gap-2 text-sm font-medium text-text-primary dark:text-gray-200">
            <Footprints className="h-4 w-4 text-text-brand dark:text-indigo-400" />
            {t("settings.profile.shoeSize")}
          </span>
          <input
            type="number"
            inputMode="numeric"
            min={20}
            max={60}
            value={shoeSize}
            onChange={(event) => setShoeSize(event.target.value)}
            className={fieldClass}
            placeholder={t("settings.profile.shoeSizePlaceholder")}
          />
        </label>
        <div className="flex min-w-0 flex-col gap-1.5">
          <span className="flex items-center gap-2 text-sm font-medium text-text-primary dark:text-gray-200">
            <Shirt className="h-4 w-4 text-text-brand dark:text-indigo-400" />
            {t("settings.profile.size")}
          </span>
          <Select value={size || undefined} onValueChange={setSize}>
            <SelectTrigger className="h-11 rounded-lg border-border-primary bg-white dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100">
              <SelectValue placeholder={t("settings.profile.selectSize")} />
            </SelectTrigger>
            <SelectContent>
              {SIZE_OPTIONS.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Button
        className="mt-6 w-full sm:w-auto"
        disabled={!ready || isSaving}
        onClick={async () => {
          const saved = await saveFields({
            age: parseInt(age, 10),
            height: parseInt(height, 10),
            size,
            shoeSize: parseInt(shoeSize, 10),
          });
          if (saved) completeQuest("fit");
        }}
      >
        {isSaving ? t("personaSetup.saving") : t("personaSetup.continue")}
      </Button>
    </QuestCard>
  );
};
